import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, GradientBorder } from '@/components/ui';
import { AppLogo } from '@/components/icons/AppLogo';

const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;

// Firebase Identity Toolkit "reset password" endpoint — the same calls the Firebase SDK's
// `verifyPasswordResetCode` / `confirmPasswordReset` make under the hood. Called directly here
// so we don't have to take a `firebase` dependency or widen the published auth-client package
// (mirrors the approach in VerifyEmailPage).
//
//  - POST { oobCode }                → verifies the code, returns the target email.
//  - POST { oobCode, newPassword }   → sets the new password (and invalidates old sessions).
const RESET_PASSWORD_URL = `https://identitytoolkit.googleapis.com/v1/accounts:resetPassword?key=${FIREBASE_API_KEY}`;

/** Minimum new-password length — matches the signup form so the two flows agree. */
const MIN_PASSWORD_LENGTH = 6;

type Status = 'verifying' | 'ready' | 'submitting' | 'success' | 'error';

/** Firebase Identity Toolkit error codes → human-readable copy. */
const ERROR_COPY: Record<string, string> = {
  EXPIRED_OOB_CODE: 'This password reset link has expired. Request a new one from the sign-in screen.',
  INVALID_OOB_CODE: 'This reset link is invalid or has already been used. Request a new one to continue.',
  USER_DISABLED: 'This account has been disabled. Please contact support for help.',
  WEAK_PASSWORD: `Please choose a stronger password — at least ${MIN_PASSWORD_LENGTH} characters.`,
};

const GENERIC_ERROR = 'We could not reset your password. Please try again.';

/** Pull the Identity Toolkit error code (e.g. "EXPIRED_OOB_CODE") out of a REST error body. */
async function readErrorCode(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
  return body?.error?.message?.split(' ')[0] ?? '';
}

/**
 * `/reset-password` route.
 *
 * Lands the user from the branded password-reset email (carrying the Firebase `oobCode`)
 * and lets them choose a new password on our own page — Crown Prediction's design rather
 * than Firebase's hosted reset handler. It renders outside the authenticated provider tree
 * (see App.tsx): the link may be opened on any device with no session.
 *
 * On mount it validates the code (and reads the target email) so an expired/used link fails
 * fast before the user types anything. Submitting confirms the reset directly against the
 * Firebase Identity Toolkit; Firebase invalidates the account's existing sessions, so the
 * user signs in fresh afterwards.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode');

  const [status, setStatus] = useState<Status>('verifying');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate the oobCode (and resolve the email) on mount so a dead link is caught up front.
  useEffect(() => {
    if (!oobCode) {
      setStatus('error');
      setError('This link is missing its reset code. Open the most recent reset email and tap the button again.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(RESET_PASSWORD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ oobCode }),
        });
        if (!res.ok) {
          const code = await readErrorCode(res);
          throw new Error(ERROR_COPY[code] ?? GENERIC_ERROR);
        }
        const body = (await res.json().catch(() => null)) as { email?: string } | null;
        if (cancelled) return;
        setEmail(body?.email ?? null);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : GENERIC_ERROR);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  async function handleReset(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!oobCode) return;

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Those passwords don’t match. Please re-enter them.');
      return;
    }

    setStatus('submitting');
    setError(null);
    try {
      const res = await fetch(RESET_PASSWORD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oobCode, newPassword: password }),
      });
      if (!res.ok) {
        const code = await readErrorCode(res);
        throw new Error(ERROR_COPY[code] ?? GENERIC_ERROR);
      }
      setStatus('success');
    } catch (err) {
      // Stay on the form (back to 'ready') so a recoverable error (e.g. weak password) can be
      // retried; only a dead code surfaced on mount lands in the terminal 'error' state.
      setStatus('ready');
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
    }
  }

  const succeeded = status === 'success';
  const deadLink = status === 'error';
  const busy = status === 'submitting' || status === 'verifying';

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden px-6 py-10">
      <ResetPasswordKeyframes />

      {/* Atmospheric brand glows behind the card — soft, on-palette, never busy. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-20 size-72 rounded-full bg-accent/25 blur-[90px]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-24 size-80 rounded-full bg-purple/20 blur-[100px]"
      />

      <div className="mb-8" style={{ animation: 'rp-fade-up 0.5s ease-out both' }}>
        <AppLogo width={150} />
      </div>

      <div
        className="relative w-full max-w-[400px] rounded-[24px] border border-border bg-surface px-7 py-10 shadow-card"
        style={{ animation: 'rp-fade-up 0.6s ease-out 0.06s both' }}
      >
        <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
          <StatusBadge status={status} />

          <h1
            className="mt-7 text-[26px] font-bold leading-tight text-primary"
            style={{ animation: 'rp-fade-up 0.5s ease-out 0.16s both' }}
          >
            {succeeded ? 'Password updated' : deadLink ? 'Link unavailable' : 'Choose a new password'}
          </h1>

          <p
            className="mt-3 max-w-[300px] text-[15px] leading-relaxed text-muted"
            style={{ animation: 'rp-fade-up 0.5s ease-out 0.22s both' }}
          >
            {succeeded
              ? 'Your password has been reset. Head back to Crown Prediction and sign in with your new password.'
              : deadLink
                ? (error ?? GENERIC_ERROR)
                : email
                  ? `Resetting the password for ${email}.`
                  : 'Pick a new password to get back to trading on the markets.'}
          </p>

          {!succeeded && !deadLink && (
            <form
              onSubmit={handleReset}
              className="mt-8 w-full"
              style={{ animation: 'rp-fade-up 0.5s ease-out 0.28s both' }}
            >
              <div className="flex flex-col gap-3 text-left">
                <PasswordField
                  id="reset-password"
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggleShow={() => setShowPassword((v) => !v)}
                  disabled={busy}
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  autoComplete="new-password"
                />
                <PasswordField
                  id="reset-password-confirm"
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  show={showPassword}
                  disabled={busy}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                className="mt-6 w-full"
                disabled={busy || password.length === 0 || confirm.length === 0}
              >
                {status === 'submitting' ? 'Updating…' : status === 'verifying' ? 'Checking link…' : 'Reset password'}
              </Button>

              {error && (
                <p className="mt-4 text-[13px] leading-relaxed text-danger-alt" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}

          {(succeeded || deadLink) && (
            <a
              href="/"
              className="mt-8 inline-flex h-12 w-full items-center justify-center rounded-full bg-gradient-to-br from-[#9d4edd] via-[#7b2cbf] to-[#6506ab] text-base font-semibold text-white hover:opacity-95"
              style={{ animation: 'rp-fade-up 0.5s ease-out 0.28s both' }}
            >
              Back to sign in
            </a>
          )}
        </div>
      </div>

      <p className="mt-6 text-[13px] text-muted" style={{ animation: 'rp-fade-up 0.5s ease-out 0.34s both' }}>
        Crown Prediction
      </p>
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow?: () => void;
  disabled?: boolean;
  placeholder?: string;
  autoComplete?: string;
}

/** Pill password input matching the auth forms, with an optional show/hide toggle. */
function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  disabled,
  placeholder,
  autoComplete,
}: PasswordFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="px-1 text-sm text-primary">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          data-testid={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          disabled={disabled}
          autoComplete={autoComplete}
          className="h-12 w-full rounded-card border border-border bg-input-soft px-4 pr-11 text-base text-primary placeholder:text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        />
        {onToggleShow && (
          <button
            type="button"
            onClick={onToggleShow}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted opacity-60 hover:opacity-100"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              {show ? (
                <path
                  d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 15a3 3 0 100-6 3 3 0 000 6z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 014.06-5.94M9.9 4.24A11 11 0 0112 4c7 0 11 8 11 8a19.77 19.77 0 01-3.16 4.34M1 1l22 22 M14.12 14.12a3 3 0 11-4.24-4.24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Hero icon badge — the page's focal point. Brand accent ring while the link is being
 * checked or the form is open, then resolves to a semantic green / red badge.
 */
function StatusBadge({ status }: { status: Status }) {
  if (status === 'success') {
    return (
      <Badge glowClass="bg-success/40" ringClass="border-2 border-success/40 bg-success/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-11 text-success" aria-hidden>
          <path
            d="M4.5 12.5l5 5 10-11"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'rp-draw 0.55s ease-out 0.18s forwards' }}
          />
        </svg>
      </Badge>
    );
  }

  if (status === 'error') {
    return (
      <Badge glowClass="bg-danger/35" ringClass="border-2 border-danger/40 bg-danger/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-10 text-danger" aria-hidden>
          <path d="M12 8v5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1.3" fill="currentColor" />
          <path d="M12 3.5l9 15.5H3l9-15.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Badge>
    );
  }

  // verifying / ready / submitting — brand accent ring with a padlock (or spinner when busy).
  const busy = status === 'verifying' || status === 'submitting';
  return (
    <div className="relative grid place-items-center" style={{ animation: 'rp-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-accent/30 blur-2xl"
        style={{ animation: 'rp-glow 3s ease-in-out infinite' }}
      />
      <GradientBorder borderWidth={2.5} className="relative grid size-24 place-items-center rounded-full bg-input-soft">
        {busy ? (
          <svg viewBox="0 0 24 24" fill="none" className="size-9 animate-spin text-accent-dark" aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2.4" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" className="size-10 text-accent-dark" aria-hidden>
            <rect x="4" y="10.5" width="16" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
            <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
          </svg>
        )}
      </GradientBorder>
    </div>
  );
}

function Badge({ glowClass, ringClass, children }: { glowClass: string; ringClass: string; children: ReactNode }) {
  return (
    <div className="relative grid place-items-center" style={{ animation: 'rp-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className={`absolute inset-0 rounded-full blur-2xl ${glowClass}`}
        style={{ animation: 'rp-glow 3s ease-in-out infinite' }}
      />
      <div className={`relative grid size-24 place-items-center rounded-full ${ringClass}`}>{children}</div>
    </div>
  );
}

/** Scoped keyframes — no motion library in this app, so animations are CSS-only. */
function ResetPasswordKeyframes() {
  return (
    <style>{`
      @keyframes rp-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      @keyframes rp-pop { 0% { opacity: 0; transform: scale(0.6); } 60% { opacity: 1; transform: scale(1.08); } 100% { transform: scale(1); } }
      @keyframes rp-glow { 0%, 100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.1); } }
      @keyframes rp-draw { to { stroke-dashoffset: 0; } }
      @media (prefers-reduced-motion: reduce) {
        [style*="rp-"] { animation: none !important; }
      }
    `}</style>
  );
}
